// src/pages/ReportIssue/components/IssueSummarySection.js
import React from "react";
import {
    Typography,
    Box,
    Grid,
    Paper,
    Chip
} from "@mui/material";
import { Description as DescriptionIcon } from "@mui/icons-material";

const IssueSummarySection = ({
                                 problemTypes = [],
                                 issues = [],
                                 selectedProblemType = "",
                                 selectedIssue = "",
                                 selectedPriority = "Medium",
                                 editorState = null
                             }) => {
    // Function to safely find problem type
    const findProblemType = () => {
        if (!problemTypes || !Array.isArray(problemTypes) || problemTypes.length === 0) {
            return 'Not specified';
        }

        const foundType = problemTypes.find(type => {
            try {
                // Handle both Map and plain object formats
                if (type instanceof Map) {
                    return type.get("id") === selectedProblemType;
                } else if (typeof type === 'object') {
                    return type.id === selectedProblemType;
                }
                return false;
            } catch (error) {
                console.error("Error finding problem type:", error);
                return false;
            }
        });

        if (foundType) {
            if (foundType instanceof Map) {
                return foundType.get("problem_type") || 'Not specified';
            } else if (typeof foundType === 'object') {
                return foundType.problem_type || 'Not specified';
            }
        }

        return 'Not specified';
    };

    // Function to safely find issue
    const findIssue = () => {
        if (!issues || !Array.isArray(issues) || issues.length === 0) {
            return 'Not specified';
        }

        const foundIssue = issues.find(issue => {
            try {
                // Handle both Map and plain object formats
                if (issue instanceof Map) {
                    return issue.get("id") === selectedIssue;
                } else if (typeof issue === 'object') {
                    return issue.id === selectedIssue;
                }
                return false;
            } catch (error) {
                console.error("Error finding issue:", error);
                return false;
            }
        });

        if (foundIssue) {
            if (foundIssue instanceof Map) {
                return foundIssue.get("problem") || 'Not specified';
            } else if (typeof foundIssue === 'object') {
                return foundIssue.problem || 'Not specified';
            }
        }

        return 'Not specified';
    };

    // Get plain text from editor state safely
    const getEditorText = () => {
        if (!editorState || !editorState.getCurrentContent || typeof editorState.getCurrentContent !== 'function') {
            return '<em style="color: #9CA3AF">No description provided</em>';
        }

        try {
            const content = editorState.getCurrentContent();
            if (content && typeof content.hasText === 'function' && content.hasText()) {
                return content.getPlainText();
            }
        } catch (error) {
            console.error("Error getting editor text:", error);
        }

        return '<em style="color: #9CA3AF">No description provided</em>';
    };

    return (
        <Box mb={4}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DescriptionIcon sx={{ color: '#3B82F6' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#3A5A78' }}>
                    Issue Summary
                </Typography>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px'
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                            Problem Type
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {findProblemType()}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                            Specific Issue
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {findIssue()}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                            Priority
                        </Typography>
                        <Chip
                            label={selectedPriority}
                            size="small"
                            sx={{
                                mt: 0.5,
                                backgroundColor: selectedPriority === 'High'
                                    ? '#FEE2E2'
                                    : selectedPriority === 'Medium'
                                        ? '#FEF3C7'
                                        : '#D1FAE5',
                                color: selectedPriority === 'High'
                                    ? '#DC2626'
                                    : selectedPriority === 'Medium'
                                        ? '#D97706'
                                        : '#059669',
                                fontWeight: 500
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            Description
                        </Typography>
                        <Box
                            sx={{
                                mt: 1,
                                p: 2,
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                maxHeight: '200px',
                                overflow: 'auto'
                            }}
                        >
                            <div dangerouslySetInnerHTML={{
                                __html: getEditorText()
                            }} />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default IssueSummarySection;